import { useEffect, useState } from "react";

export type YoutubeDurationStatus = "idle" | "loading" | "ready" | "error";

export type YoutubeDurationState = {
  durationSeconds: number | null;
  status: YoutubeDurationStatus;
};

export const YOUTUBE_DURATION_LOOKUP_TIMEOUT_MS = 8_000;

type YoutubeDurationResult = {
  durationSeconds: number | null;
  status: "ready" | "error";
  videoId: string;
};

type YoutubePlayer = {
  destroy: () => void;
  getDuration: () => number;
};

type YoutubePlayerEvent = {
  target: YoutubePlayer;
};

export type YoutubeNamespace = {
  Player: new (
    element: HTMLElement,
    options: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: (event: YoutubePlayerEvent) => void;
        onError?: () => void;
      };
    }
  ) => YoutubePlayer;
};

declare global {
  interface Window {
    YT?: YoutubeNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

export type YoutubeApiLoader = () => Promise<YoutubeNamespace>;

let iframeApiPromise: Promise<YoutubeNamespace> | null = null;

const loadYoutubeIframeApi: YoutubeApiLoader = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("YouTube iframe API is browser-only"));
  }
  if (window.YT?.Player) return Promise.resolve(window.YT);
  if (iframeApiPromise) return iframeApiPromise;

  iframeApiPromise = new Promise<YoutubeNamespace>((resolve, reject) => {
    const priorReady = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      priorReady?.();
      if (window.YT?.Player) {
        resolve(window.YT);
      } else {
        reject(new Error("YouTube iframe API was unavailable after loading"));
      }
    };

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src="https://www.youtube.com/iframe_api"]'
    );
    if (existingScript) {
      existingScript.addEventListener(
        "error",
        () => {
          existingScript.remove();
          reject(new Error("YouTube iframe API failed"));
        },
        { once: true }
      );
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.youtube.com/iframe_api";
    script.async = true;
    script.addEventListener(
      "error",
      () => {
        script.remove();
        reject(new Error("YouTube iframe API failed"));
      },
      { once: true }
    );
    document.head.appendChild(script);
  }).catch((error) => {
    iframeApiPromise = null;
    throw error;
  });

  return iframeApiPromise;
};

export const useYoutubeDuration = (
  youtubeVideoId: string | null,
  enabled: boolean,
  loadApi: YoutubeApiLoader = loadYoutubeIframeApi
): YoutubeDurationState => {
  const [result, setResult] = useState<YoutubeDurationResult | null>(null);

  useEffect(() => {
    if (!youtubeVideoId || !enabled) return;

    let cancelled = false;
    let settled = false;
    let player: YoutubePlayer | null = null;
    const mount = document.createElement("div");
    mount.setAttribute("aria-hidden", "true");
    mount.style.position = "absolute";
    mount.style.left = "-9999px";
    mount.style.top = "0";
    mount.style.width = "1px";
    mount.style.height = "1px";
    mount.style.overflow = "hidden";
    document.body.appendChild(mount);

    const finish = (
      durationSeconds: number | null,
      status: "ready" | "error"
    ) => {
      if (cancelled || settled) return;
      settled = true;
      window.clearTimeout(timeout);
      player?.destroy();
      player = null;
      mount.remove();
      setResult({
        durationSeconds,
        status,
        videoId: youtubeVideoId,
      });
    };

    const timeout = window.setTimeout(
      () => finish(null, "error"),
      YOUTUBE_DURATION_LOOKUP_TIMEOUT_MS
    );

    loadApi()
      .then((youtube) => {
        if (cancelled || settled) return;
        player = new youtube.Player(mount, {
          videoId: youtubeVideoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            playsinline: 1,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return;
              const durationSeconds = event.target.getDuration();
              if (
                Number.isFinite(durationSeconds) &&
                durationSeconds > 0
              ) {
                finish(durationSeconds, "ready");
              } else {
                finish(null, "error");
              }
            },
            onError: () => {
              finish(null, "error");
            },
          },
        });
        if (settled) {
          player.destroy();
          player = null;
          mount.remove();
        }
      })
      .catch(() => {
        finish(null, "error");
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      player?.destroy();
      mount.remove();
    };
  }, [enabled, loadApi, youtubeVideoId]);

  if (!youtubeVideoId || !enabled) {
    return { durationSeconds: null, status: "idle" };
  }
  if (result?.videoId === youtubeVideoId) {
    return {
      durationSeconds: result.durationSeconds,
      status: result.status,
    };
  }
  return { durationSeconds: null, status: "loading" };
};
